import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { Form, Button, Row, Col, Card } from 'react-bootstrap';
import { Trash2, CheckCircle2, Plus, ArrowLeft, ArrowRight } from 'lucide-react';
import { useAlert } from '../../context/AlertContext';

interface Service {
    id: number;
    name: string;
    description: string;
    duration: string;
    price: string;
}

interface Props {
    onNext: () => void;
    onBack: () => void;
}

export const ServicesStep: React.FC<Props> = ({ onNext, onBack }) => {
    const [services, setServices] = useState<Service[]>([]);
    const [formData, setFormData] = useState({ name: '', description: '', duration: '00:30:00', price: '' });
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const { showAlert } = useAlert();

    useEffect(() => {
        fetchServices();
    }, []);

    const fetchServices = async () => {
        try {
            const response = await api.get('/services/');
            setServices(response.data);
        } catch (error) {
            console.error('Error fetching services:', error);
        } finally {
            setFetching(false);
        }
    };

    const handleAdd = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            const dataToSubmit = {
                ...formData,
                name: formData.name.toUpperCase(),
                description: formData.description ? formData.description.toUpperCase() : ''
            };

            await api.post('/services/', dataToSubmit);

            setFormData({ name: '', description: '', duration: '00:30:00', price: '' });
            await fetchServices();
            setShowForm(false);
            showAlert('Serviço adicionado com sucesso!', { type: 'success' });
        } catch (error) {
            console.error('Error adding service:', error);
            showAlert('Erro ao adicionar serviço. Verifique os dados e tente novamente.', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = (id: number) => {
        showAlert('Tem certeza que deseja remover este serviço?', {
            type: 'warning',
            title: 'Remover Serviço',
            confirmable: true,
            confirmText: 'Remover',
            cancelText: 'Cancelar',
            confirmVariant: 'danger',
            onConfirm: async () => {
                try {
                    setLoading(true);
                    await api.delete(`/services/${id}/`);
                    await fetchServices();
                    showAlert('Serviço removido com sucesso!', { type: 'success' });
                } catch (error) {
                    console.error('Error deleting service:', error);
                    showAlert('Erro ao remover serviço.', { type: 'error' });
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    if (fetching) {
        return <div className="text-center p-5">Carregando...</div>;
    }

    const showAddForm = showForm || services.length === 0;

    return (
        <div>
            <div className="step-header">
                <h3>Cadastro de Serviços</h3>
                <p className="text-muted">Quais serviços sua empresa oferece? Adicione pelo menos um para continuar.</p>
            </div>

            {showAddForm ? (
                <Card className="mb-4 bg-light border-0">
                    <Card.Body>
                        <h5 className="mb-3">Adicionar Novo</h5>
                        <Form onSubmit={handleAdd}>
                            <Row>
                                <Col xs={12} md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Nome do Serviço *</Form.Label>
                                        <Form.Control
                                            value={formData.name}
                                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                            required
                                            placeholder="Ex: Corte de Cabelo"
                                            style={{ textTransform: 'uppercase' }}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col xs={6} md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Preço (R$) (Opcional)</Form.Label>
                                        <Form.Control
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            value={formData.price}
                                            onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                                            placeholder="0.00"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col xs={6} md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Duração *</Form.Label>
                                        <Form.Select
                                            value={formData.duration}
                                            onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                                            required
                                        >
                                            <option value="00:15:00">15 min</option>
                                            <option value="00:30:00">30 min</option>
                                            <option value="00:45:00">45 min</option>
                                            <option value="01:00:00">1 hora</option>
                                            <option value="01:30:00">1h30</option>
                                            <option value="02:00:00">2 horas</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Form.Group className="mb-3">
                                <Form.Label>Descrição (Opcional)</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={2}
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                />
                            </Form.Group>
                            <div className="d-grid d-md-block">
                                <Button variant="primary" type="submit" disabled={loading || !formData.name}>
                                    {loading ? 'Adicionando...' : 'Adicionar Serviço'}
                                </Button>
                            </div>
                        </Form>
                    </Card.Body>
                </Card>
            ) : (
                <Button 
                    variant="outline-primary" 
                    onClick={() => setShowForm(true)} 
                    className="mb-4 d-flex align-items-center justify-content-center gap-2 w-100 w-md-auto"
                >
                    <Plus size={20} />
                    Adicionar Serviço
                </Button>
            )}

            {services.length > 0 && (
                <div className="services-list mb-4">
                    <h5 className="mb-3">Serviços Adicionados ({services.length})</h5>
                    {services.map(svc => (
                        <div key={svc.id} className="item-card">
                            <div className="flex-grow-1 min-width-0">
                                <h6 className="mb-1 text-truncate">{svc.name}</h6>
                                <div className="text-muted small">
                                    <span className="me-3">Duração: {svc.duration.substring(0, 5)}</span>
                                    <span>Preço: R$ {parseFloat(svc.price).toFixed(2)}</span>
                                </div>
                            </div>
                            <Button variant="link" onClick={() => handleDelete(svc.id)} disabled={loading} className="text-secondary p-0 ms-2 flex-shrink-0">
                                <Trash2 size={18} strokeWidth={1.5} />
                            </Button>
                        </div>
                    ))}
                </div>
            )}

            {services.length > 0 && (
                <div className="step-navigation">
                    <div className="step-navigation-status text-success">
                        <CheckCircle2 size={20} /> Pronto para prosseguir
                    </div>
                    <div className="step-navigation-buttons">
                        <Button variant="outline-secondary" onClick={onBack} disabled={loading} className="btn-step-back">
                            <ArrowLeft size={18} /> Voltar
                        </Button>
                        <Button className="btn-primary-premium btn-step-next" onClick={onNext}>
                            Próximo Passo <ArrowRight size={18} />
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};
